<template>
  <dp-container v-if="windowWidth > 991">
    <!-- Desktop Screen -->
    <ValidationObserver ref="cl_style_form" slim>
      <form class="mx-4 mt-4">
        <section class="mb-3">
          <ProductConfigBackLink :pet-id="petId" product-type="Collar" />
        </section>
        <pet-header v-if="requiredPet" :pet="requiredPet" class="mt-2 mb-4" />
        <h1 v-if="productGroup" class="text-center">
          {{ productGroup.name }}
        </h1>

        <div v-if="selectedProduct && selectedProduct.price">
          <h2 v-if="selectedProduct.discountedPrice" class="text-center">
            <del>${{ selectedProduct.price.toFixed(2) }}</del>
            <span class="ml-2 discounted-price"><span class="sr-only">{{ $t('dp.generic.priceReducedTo') }} </span> ${{ selectedProduct.discountedPrice.toFixed(2) }}</span>
          </h2>
          <h2 v-else-if="selectedProduct.price" class="text-center">${{ selectedProduct.price.toFixed(2) }}</h2>
        </div>
        <div v-else>
          <h2 v-if="hasProductStyleDiscountedPrice && hasProductGroupPrice" class="text-center">
            <del>${{ productGroup.price.toFixed(2) }}</del>
            <span class="ml-2 discounted-price"><span class="sr-only">{{ $t('dp.generic.priceReducedTo') }} </span> ${{ productStyle.discountedPrice.toFixed(2) }}</span>
          </h2>
          <h2 v-else-if="hasProductGroupPrice" class="text-center">${{ productGroup.price.toFixed(2) }}</h2>
        </div>

        <b-row class="mt-lg-5">
          <!-- Desktop Preview Column -->
          <b-col :sm="12" :md="12" :lg="5" :xl="5">
            <carousel
              :images="images"
              :auto-slide="!color"
              :curr-image-idx="curImageIdx"
              class="mt-sm-4 mt-md-4 mt-lg-0 mt-xl-0 mb-sm-4 mb-md-4 mb-lg-0 mb-xl-0 sticky"
            />
          </b-col>
          <b-col :sm="12" :md="12" :lg="6" :xl="6" :offset-lg="1" :offset-xl="1">
            <!-- (Select Collar Colour, Hardware Colour and Size) -->
            <section class="mb-5">
              <collar-leash-color-select
                v-if="styleOptions.length > 1 || (styleOptions.length === 1 && styleOptions[0].code !== '')"
                ref="cl_style_color_select"
                v-model="style"
                class="mb-4"
                :show-name="false"
                :options="styleOptions"
                @change="onStyleChange"
              >
                <template #label>
                  <h2 class="mb-0">{{ $t('chooseYour') }} {{ $t('Color') }}/Style</h2>
                </template>
              </collar-leash-color-select>
              <collar-leash-color-select
                v-if="colorOptions.length > 1 || (colorOptions.length === 1 && colorOptions[0].name !== '')"
                v-model="color"
                class="mb-4"
                :options="colorOptions"
                auto-select-on-create
              >
                <template #label>
                  <h2 class="mb-0">{{ $t('chooseYour') }} {{ $t('buckle') }} {{ $t('Color') }}</h2>
                </template>
              </collar-leash-color-select>
              <collar-size-select ref="size-select" v-model="size" class="mb-4" :sizes="sizeOptions" />
              <!-- Personalization Preview -->
              <CollarLeashPersonalize
                v-model="personalize"
                :preview-only="false"
                :preview-height="230"
                :pet-known="!!requiredPet"
                :pet="requiredPet ? requiredPet : null"
                :is-logged-in="!!user && !!user.id"
                :pet-owner="order ? order.petOwner : null"
                :preview-show-labels="false"
                :default-phone-number="defaultPhoneNumber"
                :is-admin="isAdmin"
                :price="personalizationPrice"
                @assignToPet="assignToPet"
              />
            </section>
            <b-button id="product-style-add-to-basket" variant="primary" block :disabled="itemLoading" @click="validateBeforeAddItem">
              {{ $t('product.addToBasket') }}
              <div v-if="itemLoading" role="status" class="fa-pulse float-right mt-1">
                <fa-icon :icon="['fas', 'spinner']" class="d-flex" />
                <span class="sr-only">{{ $t('dp.buttons.loading') }}</span>
              </div>
            </b-button>

            <!-- DISCLAIMER -->
            <small v-if="productStyle && productStyle.disclaimer" class="d-block mt-3" v-html="productStyle.disclaimer" />
          </b-col>
        </b-row>
      </form>
    </ValidationObserver>
  </dp-container>
  <dp-container
    v-else-if="windowWidth <= 991 && productStyle && productStyle.id"
    class="dp-container-wrapper"
    :class="{ disabled: itemLoading }"
  >
    <!--  Mobile Screen -->
    <ValidationObserver ref="cl_style_form" slim>
      <form class="mx-4">
        <section class="mt-4">
          <section class="mb-3">
            <ProductConfigBackLink :pet-id="petId" product-type="Collar" />
          </section>
          <pet-header v-if="requiredPet" class="mt-2 mb-4" :pet="requiredPet" />
          <h1 v-if="productGroup" class="text-center">
            {{ productGroup.name }}
          </h1>
          <h2 v-if="hasProductStyleDiscountedPrice && hasProductGroupPrice" class="text-center">
            <del>${{ productGroup.price.toFixed(2) }}</del>
            <span class="ml-2 discounted-price"><span class="sr-only"> {{ $t('dp.generic.priceReducedTo') }} </span> ${{ productStyle.discountedPrice.toFixed(2) }}</span>
          </h2>
          <h2 v-else-if="hasProductGroupPrice" class="text-center">${{ productGroup.price.toFixed(2) }}</h2>
          <b-row class="mt-lg-5">
            <b-col :sm="12" :md="12" :lg="5" :xl="5">
              <carousel
                :images="images"
                :auto-slide="!color"
                :curr-image-idx="curImageIdx"
                class="mt-sm-4 mt-md-4 mt-lg-0 mt-xl-0 mb-sm-4 mb-md-4 mb-lg-0 mb-xl-0"
              />
            </b-col>
            <b-col :sm="12" :md="12" :lg="6" :xl="6" :offset-lg="1" :offset-xl="1">
              <!-- Step 1 (Select Collar Colour, Hardware Colour and Size) -->
              <section class="my-4">
                <ValidationObserver ref="cl_style_form_step_1" slim>
                  <collar-leash-color-select
                    v-if="styleOptions.length > 1 || (styleOptions.length === 1 && styleOptions[0].code !== '')"
                    ref="cl_style_color_select"
                    v-model="style"
                    class="mb-4"
                    :show-name="false"
                    :options="styleOptions"
                    @change="onStyleChange"
                  >
                    <template #label>
                      <h2 class="mb-0">{{ $t('chooseYour') }} {{ $t('Color') }}/{{ $t('style') }}</h2>
                    </template>
                  </collar-leash-color-select>
                  <collar-leash-color-select
                    v-if="colorOptions.length > 1 || (colorOptions.length === 1 && colorOptions[0].name !== '')"
                    v-model="color"
                    class="mb-4"
                    :options="colorOptions"
                    auto-select-on-create
                  >
                    <template #label>
                      <h2 class="mb-0">{{ $t('chooseYour') }} {{ $t('buckle') }} {{ $t('Color') }}</h2>
                    </template>
                  </collar-leash-color-select>
                  <collar-size-select ref="size-select" v-model="size" class="mb-4" :sizes="sizeOptions" />
                  <!-- Personalization Preview -->
                  <CollarLeashPersonalize
                    v-model="personalize"
                    :preview-only="false"
                    :preview-height="100"
                    :pet-known="!!requiredPet"
                    :pet="requiredPet ? requiredPet : null"
                    :is-logged-in="!!user && !!user.id"
                    :pet-owner="order ? order.petOwner : null"
                    :preview-show-labels="false"
                    :default-phone-number="defaultPhoneNumber"
                    :is-admin="isAdmin"
                    :price="personalizationPrice"
                    @assignToPet="assignToPet"
                  />
                </ValidationObserver>
              </section>
              <b-button id="product-style-add-to-basket" variant="primary" block :disabled="itemLoading" @click="validateBeforeAddItem">
                {{ $t('product.addToBasket') }}
                <div v-if="itemLoading" role="status" class="fa-pulse float-right mt-1">
                  <fa-icon :icon="['fas', 'spinner']" class="d-flex" />
                  <span class="sr-only">{{ $t('dp.buttons.loading') }}</span>
                </div>
              </b-button>

              <!-- DISCLAIMER -->
              <small v-if="productStyle && productStyle.disclaimer" class="d-block mt-3" v-html="productStyle.disclaimer" />
            </b-col>
          </b-row>
        </section>
      </form>
    </ValidationObserver>
  </dp-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import getImage from '@/mixins/getImage';
import { DpContainer, DpFocusableElementMixins } from '@docupet/component-library';
import PetHeader from '@/components/PetHeader';
import Carousel from '@/components/Carousel';
import CollarSizeSelect from '@/components/collar&leash/CollarSizeSelect';
import CollarLeashColorSelect from '@/components/collar&leash/CollarLeashColorSelect';
import CollarLeashPersonalize from '@/components/collar&leash/CollarLeashPersonalize';
import { NotificationMixins } from '@/mixins/notification';
import { ProductStyleAnalyticsMixins } from '@/mixins/analytics/productStyle';
import { ProductRedirectMixins } from '@/mixins/redirect';
import ProductConfigBackLink from '@/components/common/ProductConfigBackLink';

export default {
  name: 'CollarStyle',
  components: {
    CollarLeashPersonalize,
    CollarLeashColorSelect,
    CollarSizeSelect,
    Carousel,
    PetHeader,
    DpContainer,
    ProductConfigBackLink,
  },
  mixins: [getImage, NotificationMixins, ProductStyleAnalyticsMixins, ProductRedirectMixins, DpFocusableElementMixins],
  props: {
    id: {
      type: [String, Number],
      default: '',
      required: false,
    },
    petId: {
      type: [String, Number],
      default: '',
      required: false,
    },
  },
  data() {
    return {
      pet: '',
      size: '',
      style: this.productStyle?.code || '',
      color: '',
      personalize: {
        selected: false,
        valid: true,
        name: '',
        phone: '',
      },
      windowWidth: window.innerWidth,
    };
  },
  metaTag() {
    return {
      ...this.$router.currentRoute.meta,
      title: `${this.productStyle?.name || ''} -`,
      meta: [
        { charset: 'utf-8' },
        {
          vmid: 'description',
          name: 'description',
          content: this.productStyle?.description || '',
        },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: this.productStyle?.description || '',
        },
      ],
    };
  },
  computed: {
    ...mapState({
      city: (state) => state.user.city,
      cityId: (state) => state.cityId,
      orderId: (state) => state.orderId,
      baseUrl: (state) => state.baseUrl,
      loading: (state) => state.collarProduct.loading,
      itemLoading: (state) => state.order.itemLoading,
      productGroups: (state) => state.collarProduct.groups,
      productStyle: (state) => state.collarProduct.style,
      order: (state) => state.order.order,
      user: (state) => state.user.user,
      isAdmin: (state) => state.user.user.id && state.user.user.__typename == 'Administrator',
    }),
    ...mapGetters({
      getRequiredPetByPetId: 'order/getRequiredPetByPetId',
      generateFullUrl: 'generateFullUrl',
      localBasketCount: 'order/getLocalCount',
    }),
    requiredPet() {
      return this.getRequiredPetByPetId(this.petId);
    },
    productStyleCode() {
      return this.productStyle?.code;
    },
    productGroup() {
      return this.productGroups?.find((group) => group.id == this.id);
    },
    images() {
      let images = [];
      this.productGroup?.productStyles?.forEach((style) => {
        style?.products?.forEach((product) => {
          if (product?.size === '4/X') images.push(product?.image || product?.backImage);
        });
      });
      return images?.filter((image) => !!image && !!image?.original);
    },
    curImageIdx() {
      let selectedImage = this.selectedProduct?.image?.original;
      let index = null;
      if (this.images && selectedImage)
        for (let i = 0; i < this.images.length; i++) {
          if (this.images[i].original && this.images[i].original === selectedImage && !index) {
            index = i;
          }
        }
      return index || 0;
    },
    styleOptions() {
      let styles = [];
      this.productGroup?.productStyles?.forEach((style) => {
        let index = styles?.findIndex((s) => s?.code == style?.code);
        if (index === -1) {
          index = styles?.length;
          styles?.push({ name: style.code });
        }
        styles[index].image = style?.image;
        styles[index].colorOptions = this.colorOptionsFromProducts(style?.products);
        styles[index].smallInStock = !!styles[index]?.colorOptions?.some((color) => color.XSInStock || color.SInStock);
        styles[index].largeInStock = !!styles[index]?.colorOptions?.some((color) => color.LInStock || color.XLInStock);
        styles[index].allOutOfStock = !!styles[index]?.colorOptions?.every((color) => color.allOutOfStock);
      });
      return styles;
    },
    colorOptions() {
      return this.colorOptionsFromProducts(this.productStyle?.products);
    },
    sizeOptions() {
      let sizes = { XS: false, S: false, M: false, L: false, XL: false };
      let color = this.colorOptions?.find((color) => color?.name === this.color);
      if (color) {
        sizes.XS = { inStock: color?.XSInStock, ...color?.XSDetails };
        sizes.S = { inStock: color?.SInStock, ...color?.SDetails };
        sizes.M = { inStock: color?.MInStock, ...color?.MDetails };
        sizes.L = { inStock: color?.LInStock, ...color?.LDetails };
        sizes.XL = { inStock: color?.XLInStock, ...color?.XLDetails };
      }
      return sizes;
    },
    selectedProductId() {
      return this.selectedProduct?.id;
    },
    selectedProduct() {
      const mapSize = { XS: '0/T', S: '1/S', M: '2/M', L: '3/L', XL: '4/X' };
      return this.productStyle?.products?.find(
        (product) => product?.color === this.color && product?.size === mapSize[this.size] && product?.status === 'I'
      );
    },
    defaultPhoneNumber() {
      return this.city?.customerService?.phoneNumber?.replace(/-/, '')?.substring(1) || '';
    },
    personalizationPrice() {
      return this.selectedProduct?.personalization?.price ?? this.productStyle?.products?.[0]?.personalization?.price ?? 10;
    },
    hasProductGroupPrice() {
      return this.productGroup?.price;
    },
    hasProductStyleDiscountedPrice() {
      return this.productStyle?.discountedPrice;
    },
  },
  created() {
    window.addEventListener('resize', this.windowResize);
    (async () => {
      if (!this.productGroups || this.productGroups?.length === 0) {
        await this.$store.dispatch('collarProduct/fetchGroups', { cityId: this.city?.id || this.cityId });
        while (this.loading) await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    })().then(() => {
      if (!this.productStyle?.code && this.productGroups?.length !== 0) {
        this.$store
          .dispatch('collarProduct/setStyle', this.productGroups?.find((group) => group.id == this.id)?.productStyles?.[0])
          .then(() => this.$refs?.['cl_style_color_select']?.setColor(this.productStyle?.code));
      } else {
        this.$refs?.['cl_style_color_select']?.setColor(this.productStyle?.code);
      }
    });
    if (this.petId && !!this.order?.id && !this.order.groups?.map(({ pet }) => pet?.id)?.includes(this.petId)) {
      this.$router.push({ name: 'TagStore' });
    }
    // Set Personalization
    if (!this.personalize.phone || this.personalize.phone === this.defaultPhoneNumber?.toString()?.replaceAll('-', '')) {
      this.personalize.phone = this.order?.petOwner?.primaryPhone || this.defaultPhoneNumber?.toString()?.replaceAll('-', '');
    }
    this.personalize.selected = !this.user || !this.isAdmin;
  },
  destroyed() {
    window.removeEventListener('resize', this.windowResize);
  },
  methods: {
    mapProductSize(size) {
      const mapSize = { '0/T': 'XS', '1/S': 'S', '2/M': 'M', '3/L': 'L', '4/X': 'XL' };
      return mapSize[size];
    },
    productDataObject(product) {
      return {
        image: product?.image,
        backImage: product?.backImage,
        [`${this.mapProductSize(product?.size)}Details`]: {
          description: product?.sizeDescription,
          image: product?.sizeImage?.original,
          length: product?.sizeLength,
          title: product?.sizeTitle,
          width: product?.sizeWidth,
        },
        [`${this.mapProductSize(product?.size)}InStock`]: product?.status === 'I',
      };
    },
    colorOptionsFromProducts(products) {
      let colors = [];
      products?.forEach((product) => {
        let index = colors?.findIndex((color) => color?.name === product?.color);
        if (index === -1) {
          index = colors?.length;
          colors?.push({ name: product.color });
        }
        colors[index] = { ...colors[index], ...this.productDataObject(product) };
      });
      colors.forEach(
        (color) => (color['allOutOfStock'] = !color.XSInStock && !color.SInStock && !color.MInStock && !color.LInStock && !color.XLInStock)
      );
      return colors;
    },
    onStyleChange() {
      if (this.productStyleCode !== this.style && this.id) {
        this.$store.dispatch('collarProduct/setStyleByCode', { styleCode: this.style, groupId: this.id });
      }
    },
    validateBeforeAddItem() {
      this.$refs.cl_style_form
        ?.validate()
        .then(async (success) => (!success ? this.scrollToError(this.$refs.cl_style_form) : this.$nextTick(() => this.addToBasket())));
    },
    addToBasket() {
      this.addItem();
    },
    addItem() {
      if (!this.selectedProductId) return;
      this.analyticsTrackAddItem();
      if (this.user && this.user.id) {
        let details = {
          productId: this.selectedProductId,
          petId: this.requiredPet ? this.requiredPet.id : this.pet?.id,
          personalization: this.personalize.selected ? { name: '', phone: this.personalize.phone } : null,
        };

        let addItemRedirectTo = null;
        if (this.getRequiredPetByPetId() == null && this.isAdmin) {
          addItemRedirectTo = { name: 'AdminRedirect' };
        } else {
          addItemRedirectTo = !this.petId || this.isAdmin ? { name: 'CollarStore' } : { name: 'Basket' };
        }
        if (this.determineRedirectRoute) {
          addItemRedirectTo = { name: this.determineRedirectRoute };
        }
        this.$store
          .dispatch('order/addItem', {
            vm: this,
            orderId: this.orderId,
            productDetails: details,
            fetchOrderOnSuccess: !['AdminRedirect'].includes(addItemRedirectTo?.name),
          })
          .then(() => {
            if (addItemRedirectTo) {
              this.$router.push(addItemRedirectTo);
              if (!this.determineRedirectRoute) {
                this.$emit('showSideBasket');
              }
            }
          });
      } else {
        const self = this;
        const data = {
          style: this.$route.params.id,
          productId: this.selectedProductId,
          personalize: this.$data.personalize,
          color: this.$data.color,
          petId: this.$data.pet?.id,
          size: this.$data.size,
        };
        this.$store.dispatch('order/storeLocalItems', { data: [data] }).then(() => {
          this.notifySuccess(
            `Item added. <br class="d-block d-md-none" /><a href="${this.generateFullUrl(
              '/basket'
            )}" class="text-primary"><u>${self.$t('basket.viewBasket')}</u></a> | <a href="${this.generateFullUrl(
              `/login/store?redirect_path=%2Fstore`
            )}" class="text-primary"><u>${self.$t('basket.checkout')}</u></a>`,
            {
              family: 'AddItem',
              dismissSecs: 10,
              dismissible: true,
              isHtmlMessage: true,
              class: 'text-center',
              text: { class: 'text-secondary' },
              $callback: this,
              callback: function ({ localBasketCount, generateFullUrl, notifySuccess }) {
                notifySuccess(
                  `${localBasketCount} ${localBasketCount > 1 ? 'Items' : 'Item'}. <a href="${generateFullUrl(
                    '/basket'
                  )}" class="text-primary"><u>${self.$t('basket.viewBasket')}</u></a> | <a href="${generateFullUrl(
                    `/login/store?redirect_path=%2Fstore`
                  )}" class="text-primary"><u>${self.$t('basket.checkout')}</u></a>`,
                  {
                    family: 'AddItem',
                    icon: null,
                    dismissSecs: -1,
                    dismissible: true,
                    isHtmlMessage: true,
                    class: 'text-center',
                    text: { class: 'text-secondary' },
                  }
                );
              },
            }
          );
        });
        this.$router.push({ name: 'CollarStore' });
        this.$emit('showSideBasket');
      }
    },
    analyticsTrackAddItem() {
      if (window?.dataLayer && this.selectedProduct) {
        window?.dataLayer?.push({
          ecommerce: null,
        });
        const items = [];
        const _itemData = {
          quantity: 1,
          coupon: this.order?.discount?.code || '',
          currency: `${this.order?.city?.country || 'US'}D`,
          item_list_id: this.productStyle?.id || '',
          item_list_name: this.productStyle?.code?.toUpperCase() || '',
        };
        if (this.personalize?.selected) {
          items.push({
            ..._itemData,
            item_id: `${this.selectedProduct?.type?.toUpperCase()}_PERSONALIZATION`,
            item_name: `${this.selectedProduct?.type?.toUpperCase()}_PERSONALIZATION`,
            item_category: 'PERSONALIZATION',
            price: this.personalizationPrice,
          });
        }
        items.push({
          ..._itemData,
          item_id: this.selectedProduct?.id || '',
          item_name: this.selectedProduct?.name?.toUpperCase() || '',
          item_category: this.selectedProduct?.type?.toUpperCase() || '',
          price: Number(this.selectedProduct?.discountedPrice || this.selectedProduct?.price || 0).toFixed(2),
        });
        window.dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            items: items,
            currency: `${this.order?.city?.country || 'US'}D`,
            value: Number(items?.reduce((p, c) => p + Number(c.price), 0)).toFixed(2),
          },
        });
      }
    },
    assignToPet(selectedPet) {
      if (!selectedPet?.id) return;
      this.pet = selectedPet;
    },
    windowResize() {
      this.windowWidth = window.innerWidth;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
.combo-img {
  border-radius: 10px;
  border: 2px solid $dp-gray-4;
  height: auto;
  width: 100%;
}
</style>
